/**
 * Renders action execution results with status indicators and plugin-specific
 * result displays. Handles pending, success, failed, and skipped states.
 */

import { getFrontendPlugin } from "@nucleus/action-plugins/frontend";
import type { ActionExecution } from "@nucleus/shared-types";
import { AlertCircle, CheckCircle2, Circle, Clock, SkipForward } from "lucide-react";
import { Suspense, useMemo } from "react";

interface ActionWithType {
	id: string;
	name: string;
	description: string | null;
	config: Record<string, unknown> | null;
	required: boolean;
	actionType: {
		id: string;
		slug: string;
		name: string;
		type: string;
		completionType: string;
		defaultConfig?: Record<string, unknown> | null;
	} | null;
}

interface ActionExecutionRendererProps {
	/** The action configuration */
	action: ActionWithType;
	/** Current execution state (null if not started) */
	execution: ActionExecution | null;
	/** Callback when user starts the action */
	onStart: () => void;
	/** Callback when user completes the action with data */
	onComplete: (data: unknown) => void;
	/** Callback when user skips the action */
	onSkip?: (reason?: string) => void;
	/** Whether the action is currently loading/processing */
	isLoading?: boolean;
	/** Whether this action is accessible (previous actions completed) */
	isAccessible?: boolean;
}

/**
 * Get the status icon for an action execution state.
 * @param state - The execution state
 * @returns The appropriate icon component
 */
function getStateIcon(state: string | null) {
	switch (state) {
		case "completed":
			return <CheckCircle2 className="h-5 w-5 text-green-500" />;
		case "skipped":
			return <SkipForward className="h-5 w-5 text-muted-foreground" />;
		case "in_progress":
			return <Clock className="h-5 w-5 text-blue-500 animate-pulse" />;
		default:
			return <Circle className="h-5 w-5 text-muted-foreground" />;
	}
}

/**
 * Fallback component when no plugin ExecutionView is available.
 * @param props - The component props
 * @param props.action - The action configuration
 * @param props.execution - The current execution state
 * @param props.isAccessible - Whether the action is accessible
 */
function FallbackExecutionView({
	action,
	execution,
	isAccessible,
}: {
	action: ActionWithType;
	execution: ActionExecution | null;
	isAccessible?: boolean;
}) {
	const state = execution?.state ?? "pending";

	return (
		<div className="rounded-lg border p-4">
			<div className="flex items-center gap-3 mb-2">
				{getStateIcon(state)}
				<span className="font-medium">{action.name}</span>
			</div>
			{action.description && (
				<p className="text-sm text-muted-foreground mb-3">{action.description}</p>
			)}
			<div className="flex items-center gap-2 text-sm">
				<AlertCircle className="h-4 w-4 text-amber-500" />
				<span className="text-muted-foreground">
					{action.actionType
						? `No execution view available for action type: ${action.actionType.slug}`
						: "Unknown action type"}
				</span>
			</div>
			{!isAccessible && (
				<p className="text-xs text-muted-foreground mt-2">
					Complete previous actions to unlock
				</p>
			)}
		</div>
	);
}

/**
 * Renders the appropriate ExecutionView component for an action based on its type plugin.
 * @param props - component props
 * @param props.action - the action configuration
 * @param props.execution - current execution state
 * @param props.onStart - callback when user starts the action
 * @param props.onComplete - callback when user completes the action
 * @param props.onSkip - callback when user skips the action
 * @param props.isLoading - whether the action is loading
 * @param props.isAccessible - whether the action is accessible
 */
export function ActionExecutionRenderer({
	action,
	execution,
	onStart,
	onComplete,
	onSkip,
	isLoading = false,
	isAccessible = true,
}: ActionExecutionRendererProps) {
	// Get the plugin's ExecutionView component
	const pluginComponents = useMemo(() => {
		if (action.actionType?.slug) {
			return getFrontendPlugin(action.actionType.slug);
		}
		return undefined;
	}, [action.actionType?.slug]);

	// Get the effective config (action config overrides default)
	const config = useMemo(() => {
		return action.config ?? action.actionType?.defaultConfig ?? {};
	}, [action.config, action.actionType?.defaultConfig]);

	// If no plugin ExecutionView, show fallback
	if (!pluginComponents?.ExecutionView) {
		return (
			<FallbackExecutionView
				action={action}
				execution={execution}
				isAccessible={isAccessible}
			/>
		);
	}

	const { ExecutionView } = pluginComponents;

	return (
		<Suspense
			fallback={
				<div className="rounded-lg border p-4">
					<div className="animate-pulse">
						<div className="h-4 bg-muted rounded w-1/4 mb-2" />
						<div className="h-4 bg-muted rounded w-3/4" />
					</div>
				</div>
			}
		>
			<ExecutionView
				config={config}
				execution={execution}
				onStart={onStart}
				onComplete={onComplete}
				onSkip={action.required ? undefined : onSkip}
				isLoading={isLoading}
				isAccessible={isAccessible}
			/>
		</Suspense>
	);
}

/**
 * Renders a list of actions with their execution states.
 * @param props - component props
 * @param props.actions - array of actions with execution states
 * @param props.onStartAction - callback when user starts an action
 * @param props.onCompleteAction - callback when user completes an action
 * @param props.onSkipAction - callback when user skips an action
 * @param props.loadingActionId - ID of the action that is currently loading
 */
export function ActionExecutionList({
	actions,
	onStartAction,
	onCompleteAction,
	onSkipAction,
	loadingActionId,
}: {
	actions: (ActionWithType & { execution: ActionExecution | null; isAccessible: boolean })[];
	onStartAction: (actionId: string) => void;
	onCompleteAction: (actionId: string, data: unknown) => void;
	onSkipAction?: (actionId: string, reason?: string) => void;
	loadingActionId?: string;
}) {
	return (
		<div className="space-y-4">
			{actions.map((action) => (
				<ActionExecutionRenderer
					key={action.id}
					action={action}
					execution={action.execution}
					onStart={() => { onStartAction(action.id); }}
					onComplete={(data) => { onCompleteAction(action.id, data); }}
					onSkip={onSkipAction ? (reason) => { onSkipAction(action.id, reason); } : undefined}
					isLoading={loadingActionId === action.id}
					isAccessible={action.isAccessible}
				/>
			))}
		</div>
	);
}
